import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, RefreshCw, Trash2, Building2, Search, AlertTriangle, Settings2, Link2, Unlink, Wallet, ExternalLink } from 'lucide-react';
import HelpTip from '@/components/HelpTip';

const FILTERS = [
  { id: 'live', label: 'Activos' },
  { id: 'mapped', label: 'Mapeados' },
  { id: 'orphan', label: 'Huérfanos' },
  { id: 'all', label: 'Todos' },
];
const selectCls = 'w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-sm text-white';

export default function GupshupApps() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('live');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [tenants, setTenants] = useState([]);
  const [wallets, setWallets] = useState(null);
  const [consumo, setConsumo] = useState(null);
  const [consumoBusy, setConsumoBusy] = useState(false);
  const [salud, setSalud] = useState(null);
  const [saludBusy, setSaludBusy] = useState(false);

  const [manage, setManage] = useState(null);          // app en gestión
  const [subs, setSubs] = useState(null);              // {subscriptions, wired, our_webhook}
  const [mapForm, setMapForm] = useState({ company_id: '', api_key: '' });
  const [mapBusy, setMapBusy] = useState(false);
  const [wireBusy, setWireBusy] = useState(false);

  const load = async () => {
    setData(null);
    try {
      const r = await api.get('/admin/platform/gupshup/apps');
      setData(r.data || { apps: [] });
    } catch (e) {
      setData({ apps: [] });
      toast.error(e.response?.data?.detail || 'No se pudo cargar Gupshup (¿faltan credenciales Partner en Integraciones?)');
    }
  };
  const loadTenants = async () => { try { const { data } = await api.get('/admin/platform/twilio/tenants'); setTenants(data?.tenants || []); } catch { /* noop */ } };
  const loadWallets = async () => { try { const { data } = await api.get('/admin/platform/gupshup/wallets'); setWallets(data); } catch { /* noop */ } };
  const loadConsumo = async () => { setConsumoBusy(true); try { const { data } = await api.get('/admin/platform/gupshup/consumo?days=30'); setConsumo(data); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cargar el consumo'); } finally { setConsumoBusy(false); } };
  const loadSalud = async () => { setSaludBusy(true); try { const { data } = await api.get('/admin/platform/gupshup/salud'); setSalud(data); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo revisar la salud'); } finally { setSaludBusy(false); } };
  const bajaApp = async (appId, label) => { if (!window.confirm(`¿Dar de baja a "${label}"? Se desvincula de LeadCEM (el número deja de enrutar a ese tenant). Para detener el consumo por completo: bórralo si es de prueba, o pídelo a soporte de Gupshup si está activo.`)) return; try { await api.post('/admin/platform/gupshup/apps/unmap', { app_id: appId }); toast.success('Dado de baja (desvinculado)'); loadConsumo(); load(); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo dar de baja'); } };
  useEffect(() => { load(); loadTenants(); loadWallets(); }, []);

  const apps = data?.apps || [];
  const shown = useMemo(() => {
    let list = apps;
    if (filter === 'live') list = list.filter((a) => a.live);
    else if (filter === 'mapped') list = list.filter((a) => a.assigned);
    else if (filter === 'orphan') list = list.filter((a) => a.live && !a.assigned);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((a) => (a.name || '').toLowerCase().includes(s) || (a.phone || '').includes(s));
    return list;
  }, [apps, filter, q]);

  const del = async (a) => {
    if (a.assigned) { toast.error('Está asignado a un tenant. Desvincúlalo antes de borrarlo.'); return; }
    if (a.live) { toast.error('Gupshup no permite borrar apps activos por API; eso se pide a soporte de Gupshup.'); return; }
    if (!window.confirm(`Vas a BORRAR el app "${a.name}" de Gupshup. Es irreversible. ¿Continuar?`)) return;
    setBusy(a.id);
    try { await api.delete(`/admin/platform/gupshup/apps/${a.id}`); toast.success(`App "${a.name}" borrado`); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || 'No se pudo borrar'); }
    finally { setBusy(''); }
  };

  const openManage = async (a) => {
    setManage(a);
    setMapForm({ company_id: a.assigned?.company_id || '', api_key: '' });
    setSubs(null);
    try { const r = await api.get(`/admin/platform/gupshup/apps/${a.id}/subscriptions`); setSubs(r.data); }
    catch (e) { setSubs({ subscriptions: [], wired: false, error: e.response?.data?.detail || 'No se pudo leer el webhook' }); }
  };

  const doMap = async () => {
    if (!mapForm.company_id) { toast.error('Elige un cliente'); return; }
    if (!manage.phone) { toast.error('Este app no tiene número (es de prueba); no se puede asignar.'); return; }
    setMapBusy(true);
    try {
      await api.post('/admin/platform/gupshup/apps/map', {
        company_id: mapForm.company_id, app_id: manage.id, app_name: manage.name,
        source_phone: manage.phone, api_key: mapForm.api_key || undefined,
      });
      toast.success('App asignado al cliente');
      setManage(null); load(); loadTenants();
    } catch (e) { toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || 'No se pudo asignar'); }
    finally { setMapBusy(false); }
  };

  const doUnmap = async () => {
    if (!window.confirm(`Desvincular "${manage.name}" de ${manage.assigned?.name || 'su cliente'} en LeadCEM? (No borra el app en Gupshup)`)) return;
    setMapBusy(true);
    try { await api.post('/admin/platform/gupshup/apps/unmap', { app_id: manage.id }); toast.success('Desvinculado'); setManage(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo desvincular'); }
    finally { setMapBusy(false); }
  };

  const doWire = async () => {
    setWireBusy(true);
    try {
      await api.post(`/admin/platform/gupshup/apps/${manage.id}/wire`);
      toast.success('Webhook cableado a LeadCEM');
      const r = await api.get(`/admin/platform/gupshup/apps/${manage.id}/subscriptions`); setSubs(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cablear'); }
    finally { setWireBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="gupshup-apps-page">
      <div>
        <h1 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-400 shrink-0" />WhatsApp (Gupshup)
          <HelpTip title="¿Qué es esto?" content="Aquí ves TODOS los apps de WhatsApp que viven en tu cuenta de Gupshup (Partner API) y a qué cliente de LeadCEM pertenece cada uno. Puedes asignarlos a un cliente, cablear su webhook, o limpiar los de prueba." />
        </h1>
        <p className="text-xs text-muted-foreground mt-1">Lo que vive en tu cuenta Gupshup, cruzado con tus tenants de LeadCEM.</p>
        <details className="mt-2 rounded-lg border border-white/10 bg-black/20 p-3" data-testid="gupshup-guide">
          <summary className="text-xs font-semibold text-emerald-300 cursor-pointer select-none">¿Cómo leer esto?</summary>
          <ul className="mt-2 space-y-1 list-disc list-inside text-[11px] text-zinc-300 leading-relaxed">
            <li><b>Activo</b> = app en producción (con número); <b>Prueba</b> = app sin activar.</li>
            <li><b>Mapeado</b> = ya está ligado a un cliente en LeadCEM. <b>Huérfano</b> = activo en Gupshup pero NO en LeadCEM.</li>
            <li><b>Administrar</b> = asignar a un cliente / reasignar / rotar API Key y ver/cablear el webhook.</li>
            <li><b>Borrar</b> solo en apps de <b>prueba</b> (no activos) que no estén asignados. Los <b>activos</b> se borran con soporte de Gupshup.</li>
          </ul>
        </details>
      </div>

      {wallets && (wallets.wallets || []).length > 0 && (
        <Card className="bg-zinc-950/70 border-white/10" data-testid="gupshup-saldo">
          <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-white text-base flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-400 shrink-0" />Saldo de Gupshup</CardTitle>
              <CardDescription className="text-xs">Prepago, por cuenta. Si cae a {wallets.threshold} USD o menos, te avisamos por Telegram.</CardDescription>
            </div>
            <a href={wallets.portal_url} target="_blank" rel="noreferrer" className="shrink-0"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 w-full sm:w-auto"><ExternalLink className="w-3.5 h-3.5 mr-1" />Recargar en Gupshup</Button></a>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(wallets.wallets || []).map((w) => (
              <div key={w.customer_id} className={`rounded-lg border p-3 ${w.low ? 'border-red-500/40 bg-red-500/10' : 'border-white/10 bg-black/20'}`}>
                <div className="text-xs text-zinc-400 capitalize">Cuenta {w.label}</div>
                <div className={`text-2xl font-bold ${w.low ? 'text-red-400' : 'text-white'}`}>{w.balance == null ? '—' : `${w.balance.toFixed(2)} ${w.currency}`}</div>
                {w.low && <div className="text-[11px] text-red-300 mt-1">Saldo bajo — recarga pronto</div>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="bg-zinc-950/70 border-white/10" data-testid="gupshup-consumo">
        <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-white text-base">Consumo por tenant <span className="text-xs text-zinc-400">(últimos 30 días)</span></CardTitle>
            <CardDescription className="text-xs">{consumo ? `Total: ${consumo.total_cost} USD · ${consumo.consumo.length} apps con consumo` : 'Quién te gasta y cuánto. Carga bajo demanda (tarda unos segundos).'}</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={loadConsumo} disabled={consumoBusy} className="border-white/10 w-full sm:w-auto"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${consumoBusy ? 'animate-spin' : ''}`} />{consumo ? 'Refrescar' : 'Cargar consumo'}</Button>
        </CardHeader>
        {consumo && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-zinc-400 border-b border-white/10"><th className="px-4 py-2">Tenant / App</th><th className="px-4 py-2">Cuenta</th><th className="px-4 py-2 text-right">Mensajes</th><th className="px-4 py-2 text-right">Costo</th><th className="px-4 py-2"></th></tr></thead>
                <tbody>
                  {consumo.consumo.map((row) => (
                    <tr key={row.app_id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-2 text-white">{row.tenant || <span className="text-zinc-400">{row.app_name} <span className="text-[10px]">(sin mapear)</span></span>}</td>
                      <td className="px-4 py-2 text-zinc-400 capitalize">{row.customer}</td>
                      <td className="px-4 py-2 text-right text-zinc-200 tabular-nums">{row.messages}</td>
                      <td className="px-4 py-2 text-right text-emerald-300 tabular-nums">{row.cost.toFixed(2)} {row.currency}</td>
                      <td className="px-4 py-2 text-right">{row.tenant && <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10 h-7 px-2 text-xs" onClick={() => bajaApp(row.app_id, row.tenant)}><Unlink className="w-3 h-3 mr-1" />Dar de baja</Button>}</td>
                    </tr>
                  ))}
                  {consumo.consumo.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-500 text-xs">Sin consumo en el periodo.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      <Card className="bg-zinc-950/70 border-white/10" data-testid="gupshup-salud">
        <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-white text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />Salud de números</CardTitle>
            <CardDescription className="text-xs">{salud ? `${salud.problemas} con problema de ${salud.total} números` : 'Llave viva (que la IA sí entregue) y salud de cada número. Carga bajo demanda.'}</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={loadSalud} disabled={saludBusy} className="border-white/10 w-full sm:w-auto"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${saludBusy ? 'animate-spin' : ''}`} />{salud ? 'Revisar' : 'Revisar salud'}</Button>
        </CardHeader>
        {salud && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-zinc-400 border-b border-white/10"><th className="px-4 py-2">Tenant / App</th><th className="px-4 py-2">Llave (entrega)</th><th className="px-4 py-2">Salud</th></tr></thead>
                <tbody>
                  {salud.salud.map((s) => (
                    <tr key={s.app_id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-2 text-white">{s.tenant || <span className="text-zinc-400">{s.app_name}</span>}</td>
                      <td className="px-4 py-2">{s.key_ok === true ? <Badge className="bg-emerald-500/15 text-emerald-300 border-0">Viva</Badge> : s.key_ok === false ? <Badge className="bg-red-500/15 text-red-300 border-0">Muerta (401)</Badge> : <Badge className="bg-zinc-700 text-zinc-300 border-0">Sin mapear</Badge>}</td>
                      <td className="px-4 py-2">{s.healthy ? <Badge className="bg-emerald-500/15 text-emerald-300 border-0">OK</Badge> : <Badge className="bg-red-500/15 text-red-300 border-0">Enfermo</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      <Card className="bg-zinc-950/70 border-white/10">
        <CardHeader className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div><CardTitle className="text-white text-base">Apps en Gupshup</CardTitle>
              <CardDescription className="text-xs">
                {data ? `${data.total} apps · ${data.live} activos · ${data.mapped} mapeados · ${data.orphan_live} huérfanos activos` : 'Cargando…'}
              </CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={load} className="border-white/10 w-full sm:w-auto"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar</Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => (
                <button key={f.id} type="button" onClick={() => setFilter(f.id)} className={`px-3 py-1.5 rounded-lg text-xs border ${filter === f.id ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300' : 'border-white/10 bg-black/20 text-zinc-300'}`} data-testid={`gupshup-filter-${f.id}`}>{f.label}</button>
              ))}
            </div>
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o número…" className="pl-7" data-testid="gupshup-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data === null ? <p className="text-xs text-zinc-400">Cargando apps de Gupshup…</p> : shown.length === 0 ? (
            <p className="text-xs text-zinc-400">Sin apps con ese criterio.</p>
          ) : (
            <div className="space-y-2" data-testid="gupshup-apps-list">
              <p className="text-[10px] text-zinc-500">{shown.length} de {apps.length} apps</p>
              {shown.map((a) => (
                <div key={a.id} className="rounded-lg border border-white/10 bg-black/20 p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid={`gupshup-app-${a.id}`}>
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm font-medium truncate">{a.name || '(sin nombre)'}</span>
                      {a.phone && <span className="font-mono text-[11px] text-zinc-400">{a.phone}</span>}
                      {a.live ? <Badge className="bg-emerald-500/20 text-emerald-300 border-0">Activo</Badge> : <Badge className="bg-zinc-700 text-zinc-300 border-0">Prueba</Badge>}
                      {a.live && !a.healthy && <Badge className="bg-red-500/20 text-red-300 border-0">No sano</Badge>}
                    </div>
                    <div className="text-xs">
                      {a.assigned ? (
                        <span className="inline-flex items-center gap-1 text-white flex-wrap"><Building2 className="w-3 h-3 text-emerald-400 shrink-0" />{a.assigned.name}</span>
                      ) : a.live ? (
                        <span className="inline-flex items-center gap-1 text-amber-400"><AlertTriangle className="w-3 h-3" />Huérfano — activo en Gupshup pero sin cliente en LeadCEM</span>
                      ) : <span className="text-zinc-500">sin mapear</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:justify-end sm:shrink-0 items-center">
                    <Button size="sm" variant="outline" className="border-white/10" onClick={() => openManage(a)} data-testid={`gupshup-manage-${a.id}`}><Settings2 className="w-3.5 h-3.5 mr-1" />Administrar</Button>
                    {!a.assigned && !a.live && (
                      <Button size="sm" className="bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30" onClick={() => del(a)} disabled={busy === a.id} title="Borrar app de prueba"><Trash2 className="w-3.5 h-3.5 mr-1" />{busy === a.id ? 'Borrando…' : 'Borrar'}</Button>
                    )}
                    {!a.assigned && a.live && <span className="text-[10px] text-zinc-500 sm:text-right">Activo — se borra con soporte de Gupshup</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* MODAL ADMINISTRAR */}
      {manage && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => setManage(null)}>
          <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 bg-zinc-950 p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="gupshup-manage-modal">
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2"><Settings2 className="w-4 h-4 text-emerald-400 shrink-0" />{manage.name}</h3>
              <p className="text-[11px] text-zinc-400">{manage.phone || 'sin número'} · {manage.live ? 'Activo' : 'Prueba'}</p>
            </div>

            {/* Asignar / reasignar / rotar */}
            <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-3">
              <p className="text-xs font-semibold text-white flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-emerald-400" />Asignar a un cliente
                <HelpTip title="Asignar / reasignar / rotar" content="Liga este app a un cliente de LeadCEM (o cámbialo de cliente). La API Key la copias del panel de Gupshup de ese número; si ya estaba mapeado y no pones una, se conserva la actual." /></p>
              <div><Label className="text-xs">Cliente</Label>
                <select className={selectCls} value={mapForm.company_id} onChange={(e) => setMapForm({ ...mapForm, company_id: e.target.value })} data-testid="gupshup-map-tenant">
                  <option value="">Elige…</option>
                  {tenants.map((t) => <option key={t.company_id} value={t.company_id}>{t.name}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">API Key (de Gupshup){manage.assigned ? ' — vacío = conservar la actual' : ''}</Label>
                <Input type="password" value={mapForm.api_key} onChange={(e) => setMapForm({ ...mapForm, api_key: e.target.value })} placeholder="sk_…" data-testid="gupshup-map-key" />
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
                {manage.assigned
                  ? <Button variant="outline" className="border-red-500/30 text-red-300 w-full sm:w-auto" onClick={doUnmap} disabled={mapBusy}><Unlink className="w-3.5 h-3.5 mr-1" />Desvincular</Button>
                  : <span />}
                <Button className="bg-emerald-600 hover:bg-emerald-500 w-full sm:w-auto" onClick={doMap} disabled={mapBusy}>{mapBusy ? 'Guardando…' : (manage.assigned ? 'Actualizar / Reasignar' : 'Asignar')}</Button>
              </div>
            </div>

            {/* Webhook */}
            <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
              <p className="text-xs font-semibold text-white flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5 text-emerald-400" />Webhook (a dónde manda Gupshup los mensajes)
                <HelpTip title="Cablear el webhook" content="Para que los mensajes entrantes lleguen a LeadCEM, este app debe mandar sus eventos a nuestro webhook. Si ves 'Sin cablear', dale a Cablear. app.leadcem.com y app.dealider.com son el mismo sistema, ambos cuentan como cableado." /></p>
              {subs === null ? <p className="text-[11px] text-zinc-500">Leyendo webhook…</p> : (
                <>
                  <div className="text-[11px]">
                    {subs.wired ? <span className="text-emerald-400">✓ Cableado a LeadCEM</span> : <span className="text-amber-400">⚠ Sin cablear — no le llegan los mensajes a LeadCEM</span>}
                  </div>
                  {(subs.subscriptions || []).map((s, i) => (
                    <div key={i} className="text-[10px] text-zinc-400 font-mono break-all">{s.active ? '● ' : '○ '}{s.url}</div>
                  ))}
                  {!subs.wired && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 mt-1" onClick={doWire} disabled={wireBusy}><Link2 className="w-3.5 h-3.5 mr-1" />{wireBusy ? 'Cableando…' : 'Cablear a LeadCEM'}</Button>}
                </>
              )}
            </div>

            <div className="flex justify-end"><Button variant="outline" className="border-white/10" onClick={() => setManage(null)}>Cerrar</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}
